import React from 'react';
import { useEffect } from 'react';
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom';

import { Finish3dSecureCardInfo } from '../../../../gql/gql-types';
import { Card } from '../../../components/Card';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { Routes } from '../../../constants/Routes';
import { useContext } from '../../../hooks/useContext';
import { useReservationId } from '../../../hooks/useReservationId';
import { SessionStore } from '../../../stores/SessionStore';
import { ProfileUpdateAfter3DSecureStore } from '../../Accounts/AccountDetail/stores/ProfileUpdateStore';
import { ReservationTemporallyStore } from '../../Accounts/AccountsNew/AccountsNewNameInput/AccountsNewNameInputFix/stores/ReservationTemporallyStore';
import { CheckupReservationTemporallyStore } from '../../Accounts/AccountsNew/AccountsNewNameInput/AccountsNewNameInputFixCheckupIndividual/stores/CheckupReservationTemporallyStore';
import { GuestWaitingListStore } from '../../Accounts/AccountsNew/AccountsNewNameInput/AccountsNewNameInputFixWaitingListItem/stores/GuestWaitingListStore';
import { ProfileStore } from '../../Accounts/AccountsNew/stores/ProfileStore';
import { PayjpStore } from './stores/payjpStore';

/**
 * 3Dセキュア認証完了後の処理ページで別のStoreを扱うため引数で受け取る
 * mode: 'profile'→ ProfileStore
 * mode: 'profileUpdate'→ ProfileUpdateAfter3DSecureStore
 * mode: 'reservation'→ ReservationTemporallyStore
 * mode: 'checkupReservation'→ CheckupReservationTemporallyStore
 * mode: 'waitingList'→ GuestWaitingListStore
 */
type FinishPageProps = {
  store: {
    completeAfter3DSecure: (params: {
      temporallyId: string | null;
      gclid: string | null;
      waitingIdParam: string | null;
      decode: (v: string) => string;
      cardInfo?: Finish3dSecureCardInfo | null;
    }) => Promise<{
      redirectPath: string;
      fixedId?: string;
      toastMessage: string;
    }>;
  };
};

export const Payjp3dSecureFinishPage = ({ store }: FinishPageProps) => {
  const session = useContext(SessionStore.Context);
  const location = useLocation();
  const history = useHistory();
  const { addToast } = useToast();
  const { push, reservationId, decode } = useReservationId();

  useEffect(() => {
    // URLクエリパラメータを取得
    const queryParams = new URLSearchParams(location.search);
    const payjpID = queryParams.get('payjpID');
    const backTo = queryParams.get('backTo') ?? Routes.BasePath;
    const gclid = queryParams.get('gclid');
    const temporallyId = queryParams.get('temporallyId');
    const waitingIdParam = queryParams.get('waitingId');

    if (!payjpID) {
      throw new Error('PAY.JP IDが指定されていません');
    }

    (async () => {
      try {
        // 1. まずPAY.JPに3Dセキュア完了を伝える
        const cardInfo = await PayjpStore.finish3dSecure(payjpID);

        // 2. 各storeに最終フローを丸投げ
        const result = await store.completeAfter3DSecure({
          temporallyId,
          gclid,
          waitingIdParam,
          decode,
          cardInfo,
        });

        // 3. storeの結果に従って遷移＋トースト
        if (result.fixedId) {
          // fixedId（=予約IDとかwaitingListIDなど）があるフローは push でIDつき遷移
          push(result.redirectPath, result.fixedId);
        } else {
          // profileカード更新みたいにIDがいらないフローは通常遷移
          history.push(result.redirectPath || Routes.Paths.Account);
        }

        addToast({ message: result.toastMessage, icon: 'check-circle' });
      } catch (error) {
        // 3Dセキュア失敗時は元の画面に戻してからエラートーストを表示する（逆にするとトーストが2度表示される）
        console.error('エラー:', { error });
        push(backTo);
        addToast({ message: 'クレジットカードの認証でエラーが発生しました。', theme: 'error' });
      }
    })();
  }, [location.search, history, addToast, reservationId, push, store, session, decode]);

  return (
    <Modal visible>
      <Card>クレジットカードの3Dセキュア認証の完了処理をしています...</Card>
    </Modal>
  );
};

type Payjp3dSecureMode = 'profile' | 'profileUpdate' | 'reservation' | 'checkupReservation' | 'waitingList';
const payjp3dSecureModeConfigs: Record<
  Payjp3dSecureMode,
  {
    mode: Payjp3dSecureMode;
    createStore: () => FinishPageProps['store'];
  }
> = {
  profile: {
    mode: 'profile',
    createStore: () => new ProfileStore(),
  },
  profileUpdate: {
    mode: 'profileUpdate',
    createStore: () => new ProfileUpdateAfter3DSecureStore(),
  },
  reservation: {
    mode: 'reservation',
    createStore: () => new ReservationTemporallyStore(),
  },
  checkupReservation: {
    mode: 'checkupReservation',
    createStore: () => new CheckupReservationTemporallyStore(),
  },
  waitingList: {
    mode: 'waitingList',
    createStore: () => new GuestWaitingListStore(),
  },
};

export const Payjp3dSecureFinishRoute = (routeProps: RouteComponentProps) => {
  const params = new URLSearchParams(routeProps.location.search);
  const modeParam = (params.get('mode') as Payjp3dSecureMode | null) ?? 'profile';
  const config = payjp3dSecureModeConfigs[modeParam] ?? payjp3dSecureModeConfigs.profile;

  return <Payjp3dSecureFinishPage {...routeProps} store={config.createStore()} />;
};
